// services/frontend/src/components/composed/CommentForm/CommentForm.tsx
// T034: Integrated MentionAutocomplete for @-mention support
// Feature 050: Integrated markup annotation support

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { TextArea } from '../../atoms/TextArea/TextArea';
import { Button } from '../../atoms/Button/Button';
import { Label } from '../../atoms/Label/Label';
import { Text } from '../../atoms/Text/Text';
import { CharacterCounter } from '../CharacterCounter/CharacterCounter';
import { MentionAutocomplete, calculateDropdownPosition } from '../../MentionAutocomplete';
import {
  useMentionAutocomplete,
  detectMentionTrigger,
  insertMention,
  type MentionAutocompleteOption
} from '../../../hooks/useMentionAutocomplete';
import { CritiqueCommandWell } from '../CritiqueCommandWell';
import { EditSuggestionPreview } from '../EditSuggestionPreview/EditSuggestionPreview';
import { MarkupEditorModal } from '../../modals/MarkupEditorModal';
import { MarkupPreviewModal } from '../../modals/MarkupPreviewModal';
import { MarkupAttachmentPreview } from '../../markup/MarkupAttachmentPreview';
import { EditingCanvas } from '../../EditingCanvas';
import type { Photo } from '../../../types/photo';
import type { PartialCritiqueEdit, CropData, LightEditData, CombinedEditData, CritiqueEditType } from '../../../types/edit.types';
import type { MarkupShape } from '../../../types/markup.types';
import logger from '../../../utils/logger';
import { useCommandHandler } from '../../../utils/commands';
import { useAuth } from '../../../services/auth';

// Timing constants for focus management
const FOCUS_DELAY_MS = 100; // Delay for refocusing textarea after modal closes or scroll completes
const DIALOG_OPEN_DELAY_MS = 150; // Delay for opening dialogs to ensure focus is set first

export interface CommentFormSubmitData {
  content: string;
  /** Feature 050: Array of markup annotations to attach */
  markupData?: Array<{ shapes: MarkupShape[] }>;
  /** Feature 043: Edit suggestion data to attach */
  editType?: CritiqueEditType;
  editData?: CropData | LightEditData | CombinedEditData;
}

export interface CommentFormProps {
  onSubmit: (data: CommentFormSubmitData) => Promise<void>;
  onCancel?: () => void;
  isReply?: boolean;
  parentAuthor?: string;
  minLength?: number;
  maxLength?: number;
  isSubmitting?: boolean;
  error?: string;
  /** Collection ID for autocomplete context (optional) */
  collectionId?: string;
  /** Feature 071: Session ID for participant autocomplete */
  sessionId?: string;
  /** Feature 043: Photo object for edit/markup modal display */
  photo?: Photo;
}

export function CommentForm({
  onSubmit,
  onCancel,
  isReply = false,
  parentAuthor,
  minLength = 0,
  maxLength = 5000,
  isSubmitting = false,
  error,
  collectionId,
  sessionId, // Feature 071: Session ID for participant autocomplete
  photo, // Photo object for edit/markup modals
}: CommentFormProps) {
  // Feature 071: Get current user to exclude from participant suggestions
  const { user } = useAuth();

  const [content, setContent] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });

  // Feature 050: Markup annotation state management
  const [isMarkupModalOpen, setIsMarkupModalOpen] = useState(false);
  const [markupShapes, setMarkupShapes] = useState<MarkupShape[]>([]);
  const [attachedMarkup, setAttachedMarkup] = useState<Array<{ shapes: MarkupShape[] }>>([]);
  const [previewMarkupIndex, setPreviewMarkupIndex] = useState<number | null>(null);

  // Feature 043: Edit suggestion state management
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [attachedEdit, setAttachedEdit] = useState<PartialCritiqueEdit | null>(null);

  // Feature 652: Editing state for existing attachments
  const [editingMarkupIndex, setEditingMarkupIndex] = useState<number | null>(null);
  const [isEditingExistingEdit, setIsEditingExistingEdit] = useState(false);


  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const currentLength = content.trim().length;
  const isValid = currentLength >= minLength && currentLength <= maxLength;

  // Flatten all shapes from all attached markup (Feature 050)
  const allAttachedShapes = useMemo(
    () => attachedMarkup.flatMap(m => m.shapes),
    [attachedMarkup]
  );

  // Autocomplete hook
  // Feature 071: Pass sessionId for participant autocomplete, exclude current user
  const autocomplete = useMentionAutocomplete(collectionId, {
    enabled: showAutocomplete,
    attachedMarkup: allAttachedShapes, // Pass shapes for @1-@9 autocomplete
    sessionId, // Feature 071: For participant suggestions
    currentUserId: user?.id, // Feature 071: Exclude self from suggestions
  });

  // Detect mention trigger (memoized to prevent infinite loops)
  const trigger = useMemo(
    () => detectMentionTrigger(content, cursorPos),
    [content, cursorPos]
  );

  // Update autocomplete filter when trigger changes
  useEffect(() => {
    if (trigger.triggered) {
      autocomplete.setFilter(trigger.partial);
      setShowAutocomplete(true);

      // Calculate dropdown position
      if (textareaRef.current) {
        const position = calculateDropdownPosition({
          inputElement: textareaRef.current,
          triggerPos: trigger.startPos,
        });
        setAutocompletePosition(position);
      }
    } else {
      setShowAutocomplete(false);
    }
    // Note: Intentionally excluding mention list from dependencies to avoid re-triggering
  }, [trigger.triggered, trigger.partial, trigger.startPos]);

  // Handle cursor position changes
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    setCursorPos(e.target.selectionStart);
  }, []);

  const handleCursorMove = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    setCursorPos(target.selectionStart);
  }, []);


  // Handle autocomplete selection
  const handleAutocompleteSelect = useCallback((option: MentionAutocompleteOption) => {
    const result = insertMention(content, trigger, option.objectId);
    setContent(result.newText);
    setShowAutocomplete(false);

    // Focus textarea and restore cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(result.newCursorPos, result.newCursorPos);
        setCursorPos(result.newCursorPos);
      }
    }, 0);
  }, [content, trigger]);

  // Handle autocomplete cancel
  const handleAutocompleteCancel = useCallback(() => {
    setShowAutocomplete(false);
  }, []);

  // Feature 050: Markup annotation handlers
  const handleToggleMarkup = useCallback(() => {
    logger.info('CommentForm', 'Opening markup editor modal');
    setMarkupShapes([]); // Start with empty shapes for new markup
    setIsMarkupModalOpen(true);
  }, []);

  const handleMarkupCommit = useCallback(() => {
    if (markupShapes.length > 0) {
      logger.info('CommentForm', 'Committing markup with shapes:', markupShapes.length, 'editingIndex:', editingMarkupIndex);

      if (editingMarkupIndex !== null) {
        // Feature 652: Replace existing markup at the editing index
        setAttachedMarkup(prev => prev.map((m, i) =>
          i === editingMarkupIndex ? { shapes: markupShapes } : m
        ));
        setEditingMarkupIndex(null);
      } else {
        // Append new markup
        setAttachedMarkup(prev => [...prev, { shapes: markupShapes }]);
      }

      setIsMarkupModalOpen(false);
      setMarkupShapes([]); // Reset for next markup

      // Refocus textarea after modal closes
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, FOCUS_DELAY_MS);
    } else {
      logger.warn('CommentForm', 'Attempted to commit markup with no shapes');
    }
  }, [markupShapes, editingMarkupIndex]);

  const handleCloseMarkupModal = useCallback(() => {
    logger.info('CommentForm', 'Closing markup editor modal without committing');
    setIsMarkupModalOpen(false);
    setMarkupShapes([]); // Discard uncommitted changes
    setEditingMarkupIndex(null); // Feature 652: Reset editing state on cancel

    // Refocus textarea after modal closes
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  }, []);

  const handleOpenMarkupPreview = useCallback((index: number) => {
    logger.info('CommentForm', 'Opening markup preview for index:', index);
    setPreviewMarkupIndex(index);
  }, []);

  const handleCloseMarkupPreview = useCallback(() => {
    logger.info('CommentForm', 'Closing markup preview modal');
    setPreviewMarkupIndex(null);
  }, []);

  // Feature 652: Remove markup at specified index
  const handleRemoveMarkup = useCallback((index: number) => {
    logger.info('CommentForm', 'Removing markup at index:', index);
    setAttachedMarkup(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Feature 652: Remove edit suggestion
  const handleRemoveEdit = useCallback(() => {
    logger.info('CommentForm', 'Removing edit suggestion');
    setAttachedEdit(null);
  }, []);

  // Feature 652: Edit existing markup annotation
  const handleEditMarkup = useCallback((index: number) => {
    const existing = attachedMarkup[index];
    logger.info('CommentForm', 'Editing markup at index:', index, 'with shapes:', existing.shapes.length);
    setMarkupShapes(existing.shapes); // Load existing shapes into editor
    setEditingMarkupIndex(index); // Track which one we're editing
    setIsMarkupModalOpen(true);
  }, [attachedMarkup]);

  // Feature 652: Edit existing edit suggestion
  const handleEditEdit = useCallback(() => {
    logger.info('CommentForm', 'Editing existing edit suggestion:', attachedEdit?.editType);
    setIsEditingExistingEdit(true); // Flag to pass initialEdit to EditingCanvas
    setIsEditModalOpen(true);
  }, [attachedEdit]);

  // Feature 043: Edit suggestion handlers
  const handleToggleSuggestEdit = useCallback(() => {
    logger.info('CommentForm', 'Opening edit suggestion modal');
    setIsEditModalOpen(true);
  }, []);

  const handleEditCommit = useCallback((
    editType: 'crop' | 'light' | 'combined',
    editData: CropData | LightEditData | CombinedEditData,
    _comment: string // Comment is handled separately
  ) => {
    logger.info('CommentForm', 'Committing edit suggestion:', editType);
    setAttachedEdit({ editType, editData });
    setIsEditModalOpen(false);
    setIsEditingExistingEdit(false); // Feature 652: Reset editing state after commit

    // Refocus textarea after modal closes
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, FOCUS_DELAY_MS);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    logger.info('CommentForm', 'Closing edit suggestion modal without committing');
    setIsEditModalOpen(false);
    setIsEditingExistingEdit(false); // Feature 652: Reset editing state on cancel

    // Refocus textarea after modal closes
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, FOCUS_DELAY_MS);
  }, []);


  // Helper to scroll form into view and focus textarea
  const scrollIntoViewAndFocus = useCallback(() => {
    // Scroll form into view
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Focus textarea after a brief delay to ensure scroll completes
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, FOCUS_DELAY_MS);
  }, []);

  // Feature 071: Auto-scroll reply forms into view when they appear
  useEffect(() => {
    if (isReply) {
      scrollIntoViewAndFocus();
    }
  }, [isReply, scrollIntoViewAndFocus]);

  // Handle keyboard shortcuts using command system
  useCommandHandler(
    'critique.submit',
    useCallback(async () => {
      if (isValid && !isSubmitting) {
        setShowValidation(true);
        try {
          const submitData: CommentFormSubmitData = {
            content: content.trim(),
            markupData: attachedMarkup.length > 0 ? attachedMarkup : undefined,
            editType: attachedEdit?.editType,
            editData: attachedEdit?.editData,
          };
          await onSubmit(submitData);
          setContent('');
          setShowValidation(false);
          setAttachedMarkup([]);
          setAttachedEdit(null);
        } catch (err) {
          logger.error('CommentForm', 'Form submission failed:', err);
        }
      }
    }, [isValid, isSubmitting, content, attachedMarkup, attachedEdit, onSubmit]),
    isValid && !isSubmitting
  );

  useCommandHandler(
    'critique.suggestEdit',
    useCallback(() => {
      scrollIntoViewAndFocus();
      // Delay opening dialog to ensure focus is set first
      setTimeout(() => {
        handleToggleSuggestEdit();
      }, DIALOG_OPEN_DELAY_MS);
    }, [scrollIntoViewAndFocus, handleToggleSuggestEdit]),
    !!photo
  );

  useCommandHandler(
    'critique.addMarkup',
    useCallback(() => {
      scrollIntoViewAndFocus();
      // Delay opening dialog to ensure focus is set first
      setTimeout(() => {
        handleToggleMarkup();
      }, DIALOG_OPEN_DELAY_MS);
    }, [scrollIntoViewAndFocus, handleToggleMarkup]),
    !!photo
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);

    if (!isValid) return;

    try {
      // Include markup and edit data in submission
      const submitData: CommentFormSubmitData = {
        content: content.trim(),
        markupData: attachedMarkup.length > 0 ? attachedMarkup : undefined,
        editType: attachedEdit?.editType,
        editData: attachedEdit?.editData,
      };

      logger.info('CommentForm', 'Submitting form with markup:', attachedMarkup.length, 'edit:', !!attachedEdit);
      await onSubmit(submitData);

      // Reset form on success
      setContent('');
      setShowValidation(false);
      setAttachedMarkup([]); // Clear markup on success
      setAttachedEdit(null); // Clear edit on success
    } catch (err) {
      // Error is handled by parent component via error prop
      logger.error('CommentForm', 'Form submission failed:', err);
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="bg-surface-1 p-4 rounded-lg border border-border"
      aria-label={isReply ? "Reply to critique form" : "Critique submission form"}
    >
      {/* Reply header */}
      {isReply && parentAuthor && (
        <div className="mb-3 flex items-center justify-between">
          <Text variant="muted">
            Replying to <span className="font-semibold">{parentAuthor}</span>
          </Text>
          {onCancel && (
            <Button type="button" onClick={onCancel} variant="ghost" size="sm">
              Cancel
            </Button>
          )}
        </div>
      )}

      {/* Feature 043 & 050: Command well for critique enhancements */}
      {/* Note: AI critique button removed in Feature 071 - now handled via AI critique sessions */}
      {photo && (
        <CritiqueCommandWell
          isEditingSuggestion={!!attachedEdit} // Show active state when edit is attached
          onToggleSuggestEdit={handleToggleSuggestEdit} // Wire "Suggest Edit" button to local handler
          isEditingMarkup={attachedMarkup.length > 0} // Show active state when markup attached
          onToggleMarkup={handleToggleMarkup} // Wire "Markup" button to markup handler
          disabled={isSubmitting}
        />
      )}

      {/* Feature 043: Edit preview when edit is attached */}
      {attachedEdit && photo && (
        <div className="mx-4 mb-3">
          <Text variant="caption" className="text-muted mb-2 block">
            Edit suggestion attached - will be submitted with your {isReply ? 'reply' : 'critique'}:
          </Text>
          <EditSuggestionPreview
            photo={photo}
            edit={attachedEdit}
            onEdit={handleEditEdit}
            onRemove={handleRemoveEdit}
          />
        </div>
      )}

      {/* Feature 050: Markup preview thumbnails when markup attached */}
      {attachedMarkup.length > 0 && photo && (
        <div className="mx-4 mb-3">
          <Text variant="caption" className="text-muted mb-2 block">
            {attachedMarkup.length} markup annotation{attachedMarkup.length > 1 ? 's' : ''} attached - will be submitted with your {isReply ? 'reply' : 'critique'}:
          </Text>
          <div className="flex flex-wrap gap-2">
            {attachedMarkup.map((markup, index) => (
              <MarkupAttachmentPreview
                key={index}
                photo={photo}
                shapes={markup.shapes}
                onClick={() => handleOpenMarkupPreview(index)}
                onEdit={() => handleEditMarkup(index)}
                onRemove={() => handleRemoveMarkup(index)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content textarea with @-mention autocomplete */}
      <div className="mb-3 relative">
        <Label htmlFor="comment-content">
          {isReply ? 'Your Reply' : 'Your Comment'}
          <Text variant="caption" className="ml-2 text-muted">
            (Supports markdown and @-mentions)
          </Text>
        </Label>
        <TextArea
          ref={textareaRef}
          id="comment-content"
          value={content}
          onChange={handleTextChange}
          onKeyUp={handleCursorMove}
          onClick={handleCursorMove}
          rows={6}
          error={showValidation && !isValid}
          placeholder={
            isReply
              ? 'Share your thoughts... Type @ to mention photos'
              : 'Provide detailed feedback on composition, lighting, technical execution, etc. Type @ to mention photos'
          }
          aria-describedby="char-counter validation-messages"
          aria-invalid={showValidation && !isValid}
          aria-required="true"
        />

        {/* Autocomplete dropdown */}
        <MentionAutocomplete
          visible={showAutocomplete}
          options={autocomplete.options}
          isLoading={autocomplete.isLoading}
          onSelect={handleAutocompleteSelect}
          onCancel={handleAutocompleteCancel}
          position={autocompletePosition}
        />

        {/* Character counter and validation */}
        <CharacterCounter
          current={currentLength}
          min={minLength}
          max={maxLength}
          showValidation={showValidation}
        />
      </div>

      {/* Submit button */}
      <div className="flex items-center justify-between">
        <Text variant="caption" className="text-muted">
          {isReply ? 'Up to 5000 characters' : 'Thoughtful feedback helps photographers improve'}
        </Text>
        <Button
          type="submit"
          disabled={isSubmitting || (showValidation && !isValid)}
          loading={isSubmitting}
        >
          {isReply ? 'Post' : 'Post'}
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-3 p-3 bg-danger/10 border border-danger/30 rounded-lg">
          <Text variant="muted" className="text-danger">
            {error}
          </Text>
        </div>
      )}

      {/* Feature 050: Markup Editor Modal */}
      {photo && (
        <MarkupEditorModal
          isOpen={isMarkupModalOpen}
          onClose={handleCloseMarkupModal}
          photo={photo}
          shapes={markupShapes}
          onShapesChange={setMarkupShapes}
          onCommit={handleMarkupCommit}
        />
      )}

      {/* Feature 050: Markup Preview Modal */}
      {photo && previewMarkupIndex !== null && attachedMarkup[previewMarkupIndex] && (
        <MarkupPreviewModal
          isOpen={true}
          onClose={handleCloseMarkupPreview}
          photo={photo}
          shapes={attachedMarkup[previewMarkupIndex].shapes}
        />
      )}

      {/* Feature 043: Edit Suggestion Modal */}
      {photo && isEditModalOpen && (
        <EditingCanvas
          photo={photo}
          // Feature 652: Pass initialEdit when editing existing edit suggestion
          initialEdit={isEditingExistingEdit && attachedEdit ? {
            id: 'temp-edit',
            critiqueId: 'temp-critique',
            editType: attachedEdit.editType!,
            editData: attachedEdit.editData!,
            baseEditId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } : undefined}
          onSave={handleEditCommit}
          onCancel={handleCloseEditModal}
          isSaving={false}
        />
      )}
    </form>
  );
}
